/* global React */
import { Box, Grid, lighten } from '@mui/material'
import { type FC } from 'react'
import { useMemoRef, useStyles } from '@platform/react/hook'
import { capitalizeFirstLetter } from '@platform/react/hoc/children'
import DecoratedLinkBase from 'ui/Element/Button/DecoratedLink'
import Text from 'ui/Element/Text'
import Icon from 'ui/Element/Icon'

const DecoratedLink = DecoratedLinkBase as any

interface ContactChannel {
  /** the type of contact channel */
  type: string
  /** the contact channel's value */
  value: string
}

// A translated contact-channel label.
export interface ChannelLabel {
  /** the contact channel's key */
  key: string
  /** the translated label */
  value: string
}

// Information about the requester of a ticket.
export interface RequesterContact {
  /** the requester's contact channels */
  contactChannels?: ContactChannel[]
}

// Props of the NoRequest template.
interface NoRequestProps {
  /** information about the requester */
  content?: RequesterContact
  /** available prefixes for contact channel links */
  prefixes?: Record<string, string>
  /** a label to display */
  label?: string
  /** the translated contact-channel labels */
  channelLabels?: ChannelLabel[]
  /** decorators for contact channel links */
  decorators?: unknown
}

/**
 * Returns the NoRequest element's current theme styles.
 *
 * @param theme  the application's current theme
 * @returns      the NoRequest element's styles
 */
const styles = (theme: any) => ({
  root: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(10),
  },
  label: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: '1rem',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    width: '5rem',
    height: '5rem',
    backgroundColor: lighten(theme.palette.primary.main, 0.9),
    borderRadius: '50%',
    marginBottom: '1.5rem',
  },
  channel: {
    display: 'flex',
  },
  channelType: {
    fontWeight: 400,
    whiteSpace: 'nowrap!important',
    marginRight: '0.625rem',
  },
  channelValue: {
    color: theme.palette.primary.main,
  },
  ...theme.custom.noRequest,
})

/**
 * Creating an entry for each contact channel to display.
 *
 * @param p
 * @param p.classes        styles
 * @param p.prefixes       prefixes to use for linking the contact channels
 * @param p.channelLabels  translated contact channels
 * @param p.decorators     decorators for the contact channel links
 * @param p.channels       contact channels
 * @returns                the contact channel entries
 * @private
 */
const _contactChannels = ({
  classes,
  prefixes,
  decorators,
  channelLabels,
  channels,
}: {
  classes: Record<string, string>
  prefixes?: Record<string, string>
  channelLabels?: ChannelLabel[]
  decorators?: unknown
  channels: ContactChannel[]
}) => channels.map((channel, index) => {
  const channelLabel = channelLabels?.find(x => x.key === channel.type)?.value
      || capitalizeFirstLetter(channel.type as any)
  const channelType = `${channelLabel}:`

  return (
    <Box
      key={index}
      className={classes.channel}
    >
      <Text
        variant={'subtitle2'}
        className={classes.channelType}
      >
        {channelType}
      </Text>
      <DecoratedLink
        decorators={decorators}
        prefix={prefixes?.[channel.type]}
        value={channel.value}
      />
    </Box>
  )
})

/**
 * No Request Item.
 *
 * Item used to display in case there is no request for the current ticket.
 *
 * @param props               the element's properties
 * @param props.content       information about the requester
 * @param props.prefixes      prefixes to use for linking the contact channels
 * @param props.label         label to display to the user
 * @param props.channelLabels translated contact channels
 * @param props.decorators    decorators for the contact channel links
 * @returns                   the new NoRequest element
 */
const NoRequest = ({ content, prefixes, label, channelLabels, decorators }: NoRequestProps) => {
  const classes = useStyles(styles)() as Record<string, string>

  return (
    <Grid
      item
      className={classes.root}
    >
      <Box className={classes.iconContainer}>
        <Icon
          name={'phone'}
          color={'primary.main'}
          size={40}
        />
      </Box>
      <Text
        variant={'subtitle1'}
        className={classes.label}
      >
        {label}
      </Text>
      {!content?.contactChannels?.length
        ? null
        : _contactChannels({ classes,
          prefixes,
          decorators,
          channelLabels,
          channels: content.contactChannels })
      }
    </Grid>
  )
}

export default useMemoRef(NoRequest, (props: NoRequestProps) => [props.content])
